/* ============================================================
   sizing.js — Sneak Freak sizing service. Data and logic only, NO DOM.
   Ported 1:1 from the V4 prototype js/sizing.js (see HANDOVER "Sizing").

   Public interface
     window.SFSizing = {
       brands, systems, hasChart(brand), parseCm(raw), parseIn(raw), parseLength(raw, unit),
       cmToIn(cm), inToCm(in), cmPerIn,
       toCm(brand, system, value), fromCm(brand, cm), cmForLabel(brand, euLabel),
       recommend(brand, cm, sizes), convertLabel(brand, euLabel, system),
       convert({fromBrand, toBrand, system, size}), fromMeasurement(brand, raw, unit),
       checkAvailability(recommendedEu, availableSizes), table(brand),
       parseSize(label) → {system, value}   ("44 1/2 EU" → eu 44.5, "US 10.5" → us 10.5)
       euValue(label)   → EU number or null; rowFor(brand, label) → the published row or null (never interpolated)
       gender()         → 'men' for the built-ins (provenance), whatever `_meta.gender` a replacement names, else null
       apparel(view)    → general body chart for 'tops' | 'bottoms', only the measurements the data holds
       limits: {minCm, maxCm}, meta: {basis, source},
       charts()                → the active chart object (copy)
       setCharts(json)         → replace the built-in charts with the same shape
                                 (theme passes shop.metafields.custom.size_charts);
                                 returns true when accepted, false (and keeps the
                                 built-ins) when the shape is invalid.
     }
     Node: module.exports = the same object (tools can `require` it for tests).
     Also aliased as window.SF_SIZING for code ported from the prototype.

   Chart shape (also documented in assets/size-charts.json):
     { "<brand key, lower-case>": [ [euLabel:string, uk:number, usM:number, usW:number, cm:number], … ] }
     Rows MUST stay ascending by cm. Optional "generic" key = fallback for brands
     without a chart; when absent the first chart is used as generic.

   PROVENANCE: the built-in charts are transcribed from each brand's PUBLIC men's
   size chart. They are close to correct rather than authoritative — brands revise
   charts and vary by last. Replace with official data at handover (either edit
   assets/size-charts.json + this file, or set the shop metafield
   custom.size_charts with the same shape). `SFSizing.meta.source` carries the
   caveat so the UI can surface it. See sneak-freak-v4/HANDOVER.md "Sizing".

   Two rules drive the shape of this file:
   1. Conversion is keyed on FOOT LENGTH IN CENTIMETRES, never on EU-to-EU label
      arithmetic (adidas EU 42 = 25.9cm, Nike EU 42 = 26.5cm). Every lookup goes
      size -> cm -> size.
   2. One source of truth for the PDP size guide, the size-guide page and quick add.
   ============================================================ */

var SFSizing = (function () {
  'use strict';

  /* Each row: [EU label, UK, US men, US women, foot length cm]. Ascending by cm. */
  var BUILTIN = {
    /* adidas publishes in thirds of a size; the EU labels below are the ones
       printed on the box, which is what a shopper reads. */
    adidas: [
      ['38',    5,   5.5,  6.5, 23.2],
      ['38.5',  5.5, 6,    7,   23.6],
      ['39.3',  6,   6.5,  7.5, 24.1],
      ['40',    6.5, 7,    8,   24.6],
      ['40.6',  7,   7.5,  8.5, 25.0],
      ['41.3',  7.5, 8,    9,   25.5],
      ['42',    8,   8.5,  9.5, 25.9],
      ['42.6',  8.5, 9,   10,   26.3],
      ['43.3',  9,   9.5, 10.5, 26.8],
      ['44',    9.5, 10,  11,   27.2],
      ['44.6', 10,   10.5, 11.5, 27.6],
      ['45.3', 10.5, 11,  12,   28.1],
      ['46',   11,   11.5, 12.5, 28.5],
      ['46.6', 11.5, 12,  13,   29.0],
      ['47.3', 12,   12.5, 13.5, 29.4],
      /* Extended so the chart spans the whole EU 39–48 the catalogue sells;
         without this EU 47 and 48 collapsed onto the same UK label. */
      ['48',   12.5, 13,   14,   29.8],
      ['48.6', 13,   13.5, 14.5, 30.3],
      ['49.3', 13.5, 14,   15,   30.7]
    ],
    nike: [
      /* DELIBERATE DEVIATION FROM NIKE'S PRINTED CHART: Nike publishes UK 6 for
         BOTH EU 39 and EU 40. UK is stepped by the half size the 24.5 vs 25.0cm
         difference represents (38.5 -> 5, 39 -> 5.5, 40 -> 6). Every other row
         is as published. */
      ['38.5',  5,   6,    7.5, 24.0],
      ['39',    5.5, 6.5,  8,   24.5],
      ['40',    6,   7,    8.5, 25.0],
      ['40.5',  6.5, 7.5,  9,   25.5],
      ['41',    7,   8,    9.5, 26.0],
      ['42',    7.5, 8.5, 10,   26.5],
      ['42.5',  8,   9,   10.5, 27.0],
      ['43',    8.5, 9.5, 11,   27.5],
      ['44',    9,  10,   11.5, 28.0],
      ['44.5',  9.5, 10.5, 12,  28.5],
      ['45',   10,   11,  12.5, 29.0],
      ['45.5', 10.5, 11.5, 13,  29.5],
      ['46',   11,   12,  13.5, 30.0],
      ['47',   11.5, 12.5, 14,  30.5],
      ['47.5', 12,   13,  14.5, 31.0],
      ['48.5', 13,   14,  15.5, 32.0]
    ],
    'new balance': [
      ['38.5',  5.5, 6,    7.5, 24.1],
      ['39',    6,   6.5,  8,   24.6],
      ['40',    6.5, 7,    8.5, 25.0],
      ['40.5',  7,   7.5,  9,   25.4],
      ['41.5',  7.5, 8,    9.5, 25.8],
      ['42',    8,   8.5, 10,   26.2],
      ['42.5',  8.5, 9,   10.5, 26.7],
      ['43',    9,   9.5, 11,   27.1],
      ['44',    9.5, 10,  11.5, 27.5],
      ['44.5', 10,   10.5, 12,  27.9],
      ['45',   10.5, 11,  12.5, 28.3],
      ['45.5', 11,   11.5, 13,  28.8],
      ['46',   11.5, 12,  13.5, 29.2],
      ['46.5', 12,   12.5, 14,  29.7],
      ['47',   12.5, 13,  14.5, 30.1],
      ['48',   13.5, 14,  15.5, 30.9]
    ],
    puma: [
      ['39',    6,   7,    8,   24.5],
      ['40',    6.5, 7.5,  8.5, 25.0],
      ['40.5',  7,   8,    9,   25.5],
      ['41',    7.5, 8.5,  9.5, 26.0],
      ['42',    8,   9,   10,   26.5],
      ['42.5',  8.5, 9.5, 10.5, 27.0],
      ['43',    9,  10,   11,   27.5],
      ['44',    9.5, 10.5, 11.5, 28.0],
      ['44.5', 10,   11,  12,   28.5],
      ['45',   10.5, 11.5, 12.5, 29.0],
      ['46',   11,   12,  13,   29.5],
      ['46.5', 11.5, 12.5, 13.5, 30.0],
      ['47',   12,   13,  14,   30.5],
      ['48',   13,   14,  15,   31.5]
    ],
    reebok: [
      ['39',    6,   7,    8,   24.5],
      ['40',    6.5, 7.5,  8.5, 25.0],
      ['40.5',  7,   8,    9,   25.5],
      ['41',    7.5, 8.5,  9.5, 26.0],
      ['42',    8,   9,   10,   26.5],
      ['42.5',  8.5, 9.5, 10.5, 27.0],
      ['43',    9,  10,   11,   27.5],
      ['44',    9.5, 10.5, 11.5, 28.0],
      ['44.5', 10,   11,  12,   28.5],
      ['45.5', 10.5, 11.5, 12.5, 29.0],
      ['46',   11,   12,  13,   29.5],
      ['46.5', 11.5, 12.5, 13.5, 30.0],
      ['47',   12,   13,  14,   30.5],
      ['48',   13,   14,  15,   31.5]
    ]
  };

  /* Which gender's chart the built-ins are. Every built-in chart above was
     transcribed from the brand's MEN'S chart (see PROVENANCE), so the UI may
     say "men's" for them. A chart set installed through setCharts() carries
     its own `_meta.gender`, or none — the UI then names no gender. */
  var BUILTIN_GENDER = 'men';

  /* APPAREL — one general BODY chart (not garment, not brand-specific), in cm,
     [min, max] per size. Copied verbatim from the apparel table the theme
     already ships (sections/main-size-guide.liquid, setting apparel_table
     default) so the PDP sheet and the size-guide page show the same numbers.
     Only these three measurements exist in the data; the UI shows a column
     only when every size has a value for it. Override: shop metafield
     custom.size_charts may carry an "_apparel" object of the same shape. */
  var APPAREL_BUILTIN = {
    unit: 'cm',
    sizes: ['XS', 'S', 'M', 'L', 'XL', 'XXL'],
    measures: {
      chest: [[86, 91], [91, 96], [96, 101], [101, 106], [106, 111], [111, 117]],
      waist: [[71, 76], [76, 81], [81, 86], [86, 91], [91, 96], [96, 101]],
      hip:   [[86, 91], [91, 96], [96, 101], [101, 106], [106, 111], [111, 117]]
    }
  };
  /* which measurements each garment type reads, in display order */
  var APPAREL_VIEWS = { tops: ['chest', 'waist', 'hip'], bottoms: ['waist', 'hip', 'height'] };

  /* Active charts. `generic` is the fallback for a brand we hold no chart for;
     kept explicitly separate so the UI can say the recommendation is
     approximate rather than presenting it with a real chart's confidence. */
  var CHARTS = {};
  var BRANDS = [];
  var GENDER = BUILTIN_GENDER;
  var APPAREL = APPAREL_BUILTIN;

  var SYSTEMS = ['eu', 'uk', 'us-m', 'us-w', 'cm'];
  var COL = { eu: 0, uk: 1, 'us-m': 2, 'us-w': 3, cm: 4 };

  function norm(brand) {
    return String(brand || '').trim().toLowerCase();
  }

  /* Accept a brand key in any of the spellings the theme may pass:
     "New Balance", "new-balance", "newbalance", "new_balance". */
  function keyVariants(b) {
    var k = norm(b);
    return [k, k.replace(/[-_]+/g, ' '), k.replace(/[\s_-]+/g, '')];
  }

  function validRow(r) {
    return Array.isArray(r) && r.length >= 5 &&
      (typeof r[0] === 'string' || typeof r[0] === 'number') &&
      isFinite(parseFloat(r[1])) && isFinite(parseFloat(r[2])) &&
      isFinite(parseFloat(r[3])) && isFinite(parseFloat(r[4]));
  }

  /* Install a chart object. Keys are normalised to lower case; rows are copied
     and coerced (EU label -> string, others -> number) and sorted by cm. */
  function install(json) {
    if (!json || typeof json !== 'object' || Array.isArray(json)) return false;
    var next = {}, names = [], k;
    for (k in json) {
      if (!Object.prototype.hasOwnProperty.call(json, k)) continue;
      if (k.charAt(0) === '_' || k.charAt(0) === '$') continue; /* "_note" style metadata keys */
      var rows = json[k];
      if (!Array.isArray(rows) || !rows.length || !rows.every(validRow)) return false;
      var copy = rows.map(function (r) {
        return [String(r[0]), parseFloat(r[1]), parseFloat(r[2]), parseFloat(r[3]), parseFloat(r[4])];
      }).sort(function (a, b) { return a[4] - b[4]; });
      var key = norm(k);
      next[key] = copy;
      if (key !== 'generic') names.push(key);
    }
    if (!names.length) return false;
    if (!next.generic) next.generic = next[names[0]];
    /* also register the compact/hyphenated spellings so lookups by vendor
       handle work without the UI having to know the canonical key */
    names.forEach(function (n) {
      keyVariants(n).forEach(function (v) { if (!next[v]) next[v] = next[n]; });
    });
    CHARTS = next;
    BRANDS = names;
    return true;
  }

  install(BUILTIN);

  function chartFor(brand) {
    var vs = keyVariants(brand);
    for (var i = 0; i < vs.length; i++) if (CHARTS[vs[i]]) return CHARTS[vs[i]];
    return null;
  }
  function hasChart(brand) { return !!chartFor(brand); }

  /* Accept both a decimal point and a decimal comma — "27,1" is how most of
     Europe and the Levant write it. Arabic-Indic digits are normalised too. */
  function normDigits(s) {
    return String(s).replace(/[٠-٩۰-۹]/g, function (d) {
      var c = d.charCodeAt(0);
      return String(c >= 0x6f0 ? c - 0x6f0 : c - 0x660);
    }).replace(/[٫٬]/g, '.');
  }
  function parseCm(raw) {
    if (raw == null) return null;
    var s = normDigits(raw).trim().replace(',', '.');
    if (!s) return null;
    if (!/^\d{1,2}(\.\d{1,2})?$/.test(s)) return null;
    var n = parseFloat(s);
    if (!isFinite(n)) return null;
    return n;
  }

  /* ---- size LABELS as the store actually names them ---------------------
     Variant values read "44 EU", "44 1/2 EU", "36 2/3 EU"; other stores write
     "44½", "EU 44.5", "US 10.5" or "42,5". parseFloat("44 1/2 EU") is 44, which
     silently drops the half, so every lookup goes through this parser.
     Returns { system: 'eu'|'uk'|'us'|'us-m'|'us-w'|null, value: number|null }.
     system null = the label names no system (treated as EU by the callers,
     because the catalogue is EU-named). */
  var VULGAR = { '½': ' 1/2', '⅓': ' 1/3', '⅔': ' 2/3', '¼': ' 1/4', '¾': ' 3/4' };
  function parseSize(label) {
    var out = { system: null, value: null };
    if (label == null) return out;
    var s = normDigits(label).replace(/[½⅓⅔¼¾]/g, function (c) { return VULGAR[c]; });
    if (/\bUS\s*W\b|\bW\s*US\b/i.test(s)) out.system = 'us-w';
    else if (/\bUS\s*M\b|\bM\s*US\b/i.test(s)) out.system = 'us-m';
    else if (/\bUS\b/i.test(s)) out.system = 'us';
    else if (/\bUK\b/i.test(s)) out.system = 'uk';
    else if (/\bEU\b|\bEUR\b/i.test(s)) out.system = 'eu';
    var m = /(\d+(?:[.,]\d+)?)(?:\s*[-\s]\s*(\d+)\s*\/\s*(\d+))?/.exec(s);
    if (!m) return out;
    var n = parseFloat(m[1].replace(',', '.'));
    if (m[2] && m[3] && parseFloat(m[3]) > 0) n += parseFloat(m[2]) / parseFloat(m[3]);
    out.value = isFinite(n) ? n : null;
    return out;
  }
  /* The EU number a label stands for, or null when it names another system. */
  function euValue(label) {
    var p = parseSize(label);
    if (p.system && p.system !== 'eu') return null;
    return p.value;
  }
  /* Rows are at least 0.5 EU apart, so a label within 0.2 of a row can only mean
     that row: "39 1/3 EU" (39.33) is the row printed "39.3", and adidas's "38 2/3 EU"
     (38.67) is the UK 5.5 row the chart prints as "38.5". Anything further off is
     not on the chart and is never snapped to a neighbour. */
  var EU_TOLERANCE = 0.2;

  /* The published row for a label, or null when the brand prints no such size.
     Never interpolates — the size guide highlights only real rows. */
  function rowFor(brand, label) {
    var chart = chartFor(brand) || CHARTS.generic;
    if (!chart) return null;
    var i;
    for (i = 0; i < chart.length; i++) {
      if (String(chart[i][0]) === String(label).trim()) return rowObj(chart[i], i);
    }
    var want = euValue(label);
    if (want == null) return null;
    for (i = 0; i < chart.length; i++) {
      var eu = parseFloat(chart[i][0]);
      if (isFinite(eu) && Math.abs(eu - want) <= EU_TOLERANCE) return rowObj(chart[i], i);
    }
    return null;
  }
  function rowObj(r, i) { return { eu: r[0], uk: r[1], usM: r[2], usW: r[3], cm: r[4], index: i }; }

  /* ---- inches: display/input unit only; everything stored stays in cm. */
  var CM_PER_IN = 2.54;

  function cmToIn(cm) { return cm == null ? null : cm / CM_PER_IN; }
  function inToCm(inches) { return inches == null ? null : inches * CM_PER_IN; }

  /* Accept 10 1/2 and 10-1/2 as well as 10.5 and 10,5. */
  function parseIn(raw) {
    if (raw == null) return null;
    var s = normDigits(raw).trim().replace(',', '.');
    if (!s) return null;
    var frac = /^(\d{1,2})[\s-](\d)\/(\d)$/.exec(s);
    if (frac) {
      var den = parseFloat(frac[3]);
      if (!den) return null;
      return parseFloat(frac[1]) + parseFloat(frac[2]) / den;
    }
    if (!/^\d{1,2}(\.\d{1,2})?$/.test(s)) return null;
    var n = parseFloat(s);
    return isFinite(n) ? n : null;
  }

  /* One entry point for a typed length in either unit. Always returns cm. */
  function parseLength(raw, unit) {
    if (String(unit || 'cm').toLowerCase() === 'in') {
      var i = parseIn(raw);
      return i == null ? null : Math.round(inToCm(i) * 100) / 100;
    }
    return parseCm(raw);
  }

  /* Plausible human foot lengths. Outside this we say so rather than
     extrapolating off the end of a chart. */
  var CM_MIN = 20.0, CM_MAX = 33.0;

  function cmInRange(cm) { return cm >= CM_MIN && cm <= CM_MAX; }

  /* size in some system -> foot length cm, for one brand */
  function toCm(brand, system, value) {
    var chart = chartFor(brand);
    if (!chart) return null;
    var sys = String(system || 'eu').toLowerCase();
    if (sys === 'cm') return parseCm(value);
    var col = COL[sys];
    if (col == null) return null;
    if (sys === 'eu') {
      /* exact label first, then the store's own spelling ("44 1/2 EU") */
      var hit = rowFor(brand, value);
      return hit ? hit.cm : null;
    }
    var want = parseFloat(value);
    for (var i = 0; i < chart.length; i++) {
      if (Math.abs(chart[i][col] - want) < 0.001) return chart[i][4];
    }
    return null;
  }

  /* foot length cm -> the smallest size in this brand that is not shorter than
     the foot. Shoes that are slightly long are wearable; short ones are not. */
  function fromCm(brand, cm) {
    var chart = chartFor(brand);
    if (!chart || cm == null) return null;
    for (var i = 0; i < chart.length; i++) {
      if (chart[i][4] >= cm - 0.05) {
        return {
          eu: chart[i][0], uk: chart[i][1], usM: chart[i][2], usW: chart[i][3],
          cm: chart[i][4],
          easeCm: Math.round((chart[i][4] - cm) * 10) / 10
        };
      }
    }
    return null;
  }

  /* Bracketing rows for an EU label the brand does not publish. */
  function bracket(chart, want) {
    var lo = null, hi = null;
    for (var j = 0; j < chart.length; j++) {
      var eu = parseFloat(chart[j][0]);
      if (!isFinite(eu)) continue;
      if (eu <= want && (lo === null || eu > parseFloat(chart[lo][0]))) lo = j;
      if (eu >= want && (hi === null || eu < parseFloat(chart[hi][0]))) hi = j;
    }
    var extrapolated = false;
    if (lo === null) { lo = 0; hi = 1; extrapolated = true; }
    else if (hi === null) { hi = chart.length - 1; lo = chart.length - 2; extrapolated = true; }
    if (lo === hi) lo = Math.max(0, hi - 1);
    return { lo: lo, hi: hi, extrapolated: extrapolated };
  }

  /* ---- displaying one catalogue size in another system -----------------
     The catalogue sells whole EU sizes; adidas works in thirds, so only some
     labels have an exact row. Snapping to the NEAREST row makes two adjacent
     sizes print the same UK number, so interpolate between the two published
     rows that bracket the label and round to the nearest half.
     Returns { value, exact, extrapolated, cm } or null. */
  function convertLabel(brand, euLabel, system) {
    var chart = chartFor(brand) || CHARTS.generic;
    var sys = String(system || 'eu').toLowerCase();
    if (sys === 'eu') {
      var direct = toCm(brand, 'eu', euLabel);
      return {
        value: String(euLabel), exact: direct != null, extrapolated: false,
        cm: direct != null ? direct : cmForLabel(brand, euLabel)
      };
    }

    var col = COL[sys];
    if (col == null) return null;

    var want = euValue(euLabel);
    if (want == null || !isFinite(want)) return null;

    var exactRow = rowFor(brand, euLabel);
    if (exactRow) {
      var vals = [exactRow.eu, exactRow.uk, exactRow.usM, exactRow.usW, exactRow.cm];
      return { value: vals[col], exact: true, extrapolated: false, cm: exactRow.cm };
    }

    var b = bracket(chart, want);
    if (b.lo === b.hi) return null;
    var euLo = parseFloat(chart[b.lo][0]), euHi = parseFloat(chart[b.hi][0]);
    if (euHi === euLo) return null;
    var t = (want - euLo) / (euHi - euLo);

    var raw = chart[b.lo][col] + t * (chart[b.hi][col] - chart[b.lo][col]);
    var cmRaw = chart[b.lo][4] + t * (chart[b.hi][4] - chart[b.lo][4]);

    return {
      value: Math.round(raw * 2) / 2,
      exact: false,
      extrapolated: b.extrapolated,
      cm: Math.round(cmRaw * 10) / 10
    };
  }

  /* Foot length for any EU label, published or not, by interpolating the
     brand's own cm column. */
  function cmForLabel(brand, euLabel) {
    var chart = chartFor(brand) || CHARTS.generic;
    var want = euValue(euLabel);
    if (want == null || !isFinite(want)) return null;

    var exactRow = rowFor(brand, euLabel);
    if (exactRow) return exactRow.cm;

    var b = bracket(chart, want);
    if (b.lo === b.hi) return chart[b.hi][4];
    var euLo = parseFloat(chart[b.lo][0]), euHi = parseFloat(chart[b.hi][0]);
    if (euHi === euLo) return chart[b.hi][4];
    var t = (want - euLo) / (euHi - euLo);
    return Math.round((chart[b.lo][4] + t * (chart[b.hi][4] - chart[b.lo][4])) * 10) / 10;
  }

  /* THE recommendation the UI should use: picks from the sizes ACTUALLY SOLD,
     smallest offered size that is not shorter than the foot. */
  function recommend(brand, cm, sizes) {
    if (cm == null || !isFinite(cm)) return null;
    var seen = {};
    var list = (sizes || []).map(String).filter(function (s) {
      if (seen[s]) return false;
      seen[s] = 1;
      var v = euValue(s);
      return v != null && isFinite(v);
    });
    if (!list.length) return null;

    var rows = list.map(function (s) {
      return { size: s, cm: cmForLabel(brand, s) };
    }).filter(function (r) { return r.cm != null; })
      .sort(function (a, b) { return a.cm - b.cm; });

    if (!rows.length) return null;

    for (var i = 0; i < rows.length; i++) {
      if (rows[i].cm >= cm - 0.05) {
        return {
          ok: true,
          size: rows[i].size,
          cm: rows[i].cm,
          footCm: Math.round(cm * 10) / 10,
          easeCm: Math.round((rows[i].cm - cm) * 10) / 10,
          approximate: !hasChart(brand),
          aboveRange: false
        };
      }
    }
    var top = rows[rows.length - 1];
    return {
      ok: true, size: top.size, cm: top.cm,
      footCm: Math.round(cm * 10) / 10,
      easeCm: Math.round((top.cm - cm) * 10) / 10,
      approximate: !hasChart(brand),
      aboveRange: true
    };
  }

  /* The headline call: "I take a 9 in Nike, what am I in this?" */
  function convert(opts) {
    opts = opts || {};
    var from = norm(opts.fromBrand), to = norm(opts.toBrand);
    var approx = false;

    if (!hasChart(from)) { approx = true; }
    if (!hasChart(to)) { return { ok: false, reason: 'no-target-chart', brand: opts.toBrand }; }

    var cm = toCm(hasChart(from) ? from : 'generic', opts.system || 'eu', opts.size);
    if (cm == null) return { ok: false, reason: 'unknown-source-size', size: opts.size, system: opts.system };
    if (!cmInRange(cm)) return { ok: false, reason: 'out-of-range', cm: cm, min: CM_MIN, max: CM_MAX };

    var hit = fromCm(to, cm);
    if (!hit) return { ok: false, reason: 'out-of-chart', cm: cm };

    return {
      ok: true, approximate: approx,
      footCm: Math.round(cm * 10) / 10,
      size: hit.eu, uk: hit.uk, usM: hit.usM, usW: hit.usW,
      easeCm: hit.easeCm,
      fromBrand: opts.fromBrand, toBrand: opts.toBrand
    };
  }

  /* The measurement flow: a foot length straight to a size in this brand.
     `unit` is 'cm' (default) or 'in'. */
  function fromMeasurement(brand, raw, unit) {
    var cm = parseLength(raw, unit);
    if (cm == null) return { ok: false, reason: 'invalid-input', unit: unit || 'cm' };
    if (!cmInRange(cm)) return { ok: false, reason: 'out-of-range', cm: cm, min: CM_MIN, max: CM_MAX };
    var target = hasChart(brand) ? brand : 'generic';
    var hit = fromCm(target, cm);
    if (!hit) return { ok: false, reason: 'out-of-chart', cm: cm };
    return {
      ok: true, approximate: !hasChart(brand),
      footCm: Math.round(cm * 10) / 10,
      size: hit.eu, uk: hit.uk, usM: hit.usM, usW: hit.usW, easeCm: hit.easeCm, brand: brand
    };
  }

  /* Availability is checked AFTER the recommendation, never folded into it.
     Returns the nearest stocked size as a separate suggestion. */
  function checkAvailability(recommendedEu, availableSizes) {
    var list = (availableSizes || []).map(function (s) { return String(s).trim(); });
    if (!list.length) return { known: false };
    if (list.indexOf(String(recommendedEu)) > -1) return { known: true, inStock: true, size: String(recommendedEu) };
    var want = euValue(recommendedEu);
    if (want == null) return { known: true, inStock: false, size: String(recommendedEu), nearest: null, deltaSizes: Infinity };
    var nearest = null, best = Infinity;
    list.forEach(function (s) {
      var n = euValue(s);
      if (n == null || !isFinite(n)) return;
      var d = Math.abs(n - want);
      if (d < best) { best = d; nearest = s; }
    });
    return { known: true, inStock: false, size: String(recommendedEu), nearest: nearest, deltaSizes: best };
  }

  /* The reference table the size guide renders. One brand at a time. */
  function table(brand) {
    var chart = chartFor(brand) || CHARTS.generic;
    return chart.map(function (r) {
      return { eu: r[0], uk: r[1], usM: r[2], usW: r[3], cm: r[4] };
    });
  }

  /* Copy of the active charts keyed by canonical brand (+ generic). */
  function charts() {
    var out = {};
    BRANDS.concat(['generic']).forEach(function (k) {
      out[k] = CHARTS[k].map(function (r) { return r.slice(); });
    });
    return out;
  }

  /* Replace the built-in charts. Accepts an object or a JSON string (metafield
     value). Invalid input is rejected and the current charts are kept. */
  function setCharts(json) {
    var data = json;
    if (typeof data === 'string') {
      try { data = JSON.parse(data); } catch (e) { return false; }
    }
    var ok = install(data);
    if (ok) {
      API.brands = BRANDS.slice();
      /* a replacement chart set names its own gender, or none */
      var g = data._meta && typeof data._meta.gender === 'string' ? norm(data._meta.gender) : '';
      GENDER = /^(men|women|unisex|kids)$/.test(g) ? g : null;
      if (data._apparel && validApparel(data._apparel)) APPAREL = data._apparel;
    }
    return ok;
  }

  function validApparel(a) {
    if (!a || !Array.isArray(a.sizes) || !a.sizes.length || !a.measures || typeof a.measures !== 'object') return false;
    return Object.keys(a.measures).every(function (k) {
      var col = a.measures[k];
      return Array.isArray(col) && col.length === a.sizes.length;
    });
  }

  /* The body chart for one garment view ('tops' | 'bottoms'): only the
     measurements the data actually holds for EVERY size, in display order.
     Returns { unit, columns: ['chest', …], rows: [{ size, values: [[min,max]|number, …] }] }. */
  function apparel(view) {
    var want = APPAREL_VIEWS[view] || APPAREL_VIEWS.tops;
    var a = APPAREL;
    var cols = want.filter(function (k) {
      var col = a.measures[k];
      return Array.isArray(col) && col.length === a.sizes.length && col.every(function (v) {
        return Array.isArray(v) ? v.length === 2 && isFinite(v[0]) && isFinite(v[1]) : isFinite(v);
      });
    });
    return {
      unit: a.unit || 'cm',
      columns: cols,
      rows: a.sizes.map(function (size, i) {
        return { size: String(size), values: cols.map(function (k) { return a.measures[k][i]; }) };
      })
    };
  }

  /* 'men' | 'women' | 'unisex' | 'kids' | null — whose chart the active set is */
  function gender() { return GENDER; }

  var API = {
    brands: BRANDS.slice(),
    systems: SYSTEMS.slice(),
    hasChart: hasChart,
    parseCm: parseCm,
    parseIn: parseIn,
    parseLength: parseLength,
    cmToIn: cmToIn,
    inToCm: inToCm,
    cmPerIn: CM_PER_IN,
    toCm: toCm,
    fromCm: fromCm,
    cmForLabel: cmForLabel,
    recommend: recommend,
    convertLabel: convertLabel,
    convert: convert,
    fromMeasurement: fromMeasurement,
    checkAvailability: checkAvailability,
    table: table,
    rowFor: rowFor,
    parseSize: parseSize,
    euValue: euValue,
    gender: gender,
    apparel: apparel,
    charts: charts,
    setCharts: setCharts,
    limits: { minCm: CM_MIN, maxCm: CM_MAX },
    meta: {
      basis: 'foot length in centimetres',
      source: 'Transcribed from each brand’s published men’s chart. Close but not authoritative — replace with official data at handover.'
    }
  };
  return API;
})();

if (typeof window !== 'undefined') { window.SFSizing = SFSizing; window.SF_SIZING = SFSizing; }
if (typeof module !== 'undefined' && module.exports) module.exports = SFSizing;
